"use client";

import { Card, CardHeader, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pin, MoreVertical, Trash2, Eye, Edit, LayoutGrid } from "lucide-react";
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Aggregated Compliance Preview for Stack - NOW USES PROPS INSTEAD OF API CALLS
// This eliminates multiple API calls per stack (was making N calls per project in stack)
const StackCompliancePreview = ({ complianceSummary, isDarkBackground }) => {
  // Use compliance data passed from parent (pre-computed and aggregated)
  const certData = {
    valid: complianceSummary?.valid || 0,
    expiring: complianceSummary?.expiring || 0,
    expired: complianceSummary?.expired || 0,
    missing: complianceSummary?.missing || 0,
    total: complianceSummary?.total_required || 0
  };

  const aggregatedCompliance = complianceSummary?.percentage || 0;

  // Always show stats, even if all zeros
  return (
    <div className="space-y-1 w-full">
      {/* Certificate Status Cards */}
      <div className="grid grid-cols-4 gap-1">
        <div className={`rounded p-1 border ${
          isDarkBackground
            ? 'bg-green-500/20 border-green-400/30'
            : 'bg-green-50 border-green-200'
        }`}>
          <div className={`text-sm font-bold ${
            isDarkBackground ? 'text-green-200' : 'text-green-700'
          }`}>{certData.valid}</div>
          <div className={`text-[7px] leading-tight ${
            isDarkBackground ? 'text-green-300' : 'text-green-600'
          }`}>Valid</div>
        </div>
        <div className={`rounded p-1 border ${
          isDarkBackground
            ? 'bg-amber-500/20 border-amber-400/30'
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className={`text-sm font-bold ${
            isDarkBackground ? 'text-amber-200' : 'text-amber-700'
          }`}>{certData.expiring}</div>
          <div className={`text-[7px] leading-tight ${
            isDarkBackground ? 'text-amber-300' : 'text-amber-600'
          }`}>Expiring</div>
        </div>
        <div className={`rounded p-1 border ${
          isDarkBackground
            ? 'bg-red-500/20 border-red-400/30'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className={`text-sm font-bold ${
            isDarkBackground ? 'text-red-200' : 'text-red-700'
          }`}>{certData.expired}</div>
          <div className={`text-[7px] leading-tight ${
            isDarkBackground ? 'text-red-300' : 'text-red-600'
          }`}>Expired</div>
        </div>
        <div className={`rounded p-1 border ${
          isDarkBackground
            ? 'bg-gray-500/20 border-gray-400/30'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className={`text-sm font-bold ${
            isDarkBackground ? 'text-gray-200' : 'text-gray-700'
          }`}>{certData.missing}</div>
          <div className={`text-[7px] leading-tight ${
            isDarkBackground ? 'text-gray-300' : 'text-gray-600'
          }`}>Missing</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-0.5">
        <div className={`flex justify-between text-[9px] ${
          isDarkBackground ? 'text-gray-300' : 'text-gray-600'
        }`}>
          <span>Compliance Rate</span>
          <span className="font-medium">{aggregatedCompliance}%</span>
        </div>
        <div className={`h-1.5 rounded-full overflow-hidden ${
          isDarkBackground ? 'bg-white/20' : 'bg-gray-200'
        }`}>
          <div
            className={`h-full transition-all duration-500 ${
              aggregatedCompliance >= 80 ? 'bg-green-500' :
              aggregatedCompliance >= 50 ? 'bg-amber-500' :
              'bg-red-500'
            }`}
            style={{ width: `${aggregatedCompliance}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export function StackCard({ stack, onClick, isDropTarget, onColorChange, onPin, onDisband, onEdit }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Preset colors for quick selection
  const presetColors = [
    { name: 'White', value: '#FFFFFF' },
    { name: 'Yellow', value: '#FEF08A' },
    { name: 'Orange', value: '#FED7AA' },
    { name: 'Pink', value: '#FECACA' },
    { name: 'Rose', value: '#F9A8D4' },
    { name: 'Purple', value: '#DDD6FE' },
    { name: 'Blue', value: '#BFDBFE' },
    { name: 'Green', value: '#BBF7D0' },
    { name: 'Gray', value: '#E5E7EB' },
    { name: 'Slate', value: '#CBD5E1' },
  ];

  // Helper function to determine if a color is dark
  const isColorDark = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Using luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const isDarkBackground = isColorDark(stack.color?.value);

  const handleColorChange = (colorValue) => {
    const colorObj = {
      name: 'custom',
      value: colorValue
    };
    onColorChange?.(stack.id, colorObj);
  };

  return (
    <div 
      className={`relative w-full h-[200px] group cursor-pointer ${
        isDropTarget ? 'scale-105' : ''
      } transition-transform duration-200`} 
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Stacked Background Cards - One for each space */}
      {stack.projects.map((project, index) => {
        // Only show up to 5 cards to prevent excessive stacking
        if (index >= 5) return null;
        
        const cardIndex = index + 1; // Start from 1 (main card is 0)
        const rotation = 2 + (cardIndex * 1.5); // Increasing rotation
        const offsetY = cardIndex * 2; // Slight vertical offset
        const offsetX = cardIndex * 1; // Slight horizontal offset
        const scale = 1 - (cardIndex * 0.02); // Slight scale reduction
        const opacity = 0.8 - (cardIndex * 0.1); // Decreasing opacity
        
        return (
          <Card 
            key={`stack-card-${cardIndex}`}
            className={`absolute inset-0 h-[200px] transform transition-all duration-500 ease-out group-hover:rotate-6 ${
              isDropTarget ? 'bg-blue-100 ring-2 ring-blue-400' : 'bg-gray-50'
            }`}
            style={{
              backgroundColor: stack.color?.value ? `${stack.color.value}${Math.floor(opacity * 20).toString(16).padStart(2, '0')}` : undefined,
              transform: `rotate(${rotation}deg) translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
              zIndex: -cardIndex,
              opacity: Math.max(opacity, 0.3),
              boxShadow: `0 ${cardIndex * 2}px ${cardIndex * 4}px rgba(0, 0, 0, ${0.1 - cardIndex * 0.02})`
            }}
          />
        );
      })}
      
      {/* Main Card */}
      <Card
        className={`pb-0 w-full h-[200px] flex flex-col relative group shadow-md hover:shadow-xl transition-shadow duration-500 ease-in-out cursor-pointer transform rotate-0 z-10 ${
          isDropTarget ? 'bg-blue-50 ring-2 ring-blue-500' : ''
        }`}
        style={{
          backgroundColor: stack.color?.value || 'white'
        }}
      >
        {/* 3-Dot Menu - Only visible on hover */}
        <div
          className={`absolute top-2 right-2 z-20 transition-opacity duration-200 ${
            isHovered || isDropdownOpen ? 'opacity-100' : 'opacity-0'
          }`}
          data-no-dnd="true"
        >
          <DropdownMenu modal={false} open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${isDarkBackground ? 'hover:bg-white/20' : 'hover:bg-black/10'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                data-no-dnd="true"
              >
                <MoreVertical className={`h-4 w-4 ${isDarkBackground ? 'text-white' : 'text-gray-600'}`} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onPin?.(stack);
                  setIsDropdownOpen(false);
                }}
              >
                <Pin className={`h-4 w-4 mr-2 ${stack.isPinned ? 'fill-blue-600 text-blue-600' : ''}`} />
                {stack.isPinned ? 'Unpin' : 'Pin'}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onClick?.();
                  setIsDropdownOpen(false);
                }}
              >
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onEdit?.(stack);
                  setIsDropdownOpen(false);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>

              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="gap-2">
                  <div className="w-4 h-4 rounded border border-gray-300 mr-2" style={{ backgroundColor: stack.color?.value || '#FFFFFF' }} />
                  Colour
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-48">
                  <div className="p-2 space-y-2">
                    {/* Preset Colors Grid */}
                    <div className="grid grid-cols-5 gap-1">
                      {presetColors.map((color) => (
                        <button
                          key={color.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleColorChange(color.value);
                          }}
                          className={`w-7 h-7 rounded border-2 hover:border-gray-400 transition-colors ${
                            color.value === '#FFFFFF' ? 'border-gray-300' : 'border-gray-200'
                          } ${stack.color?.value === color.value ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>

                    {/* Custom Color Picker */}
                    <div className="border-t pt-2">
                      <label className="text-xs text-gray-600 mb-1 block">Custom Colour</label>
                      <input
                        type="color"
                        value={stack.color?.value || '#FFFFFF'}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleColorChange(e.target.value);
                        }}
                        className="w-full h-8 rounded cursor-pointer border border-gray-300"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('🔥 DISBAND CLICKED for stack:', stack.name, 'id:', stack.id);
                  onDisband?.(stack.id);
                  setIsDropdownOpen(false);
                }}
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Disband Stack
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <CardHeader className="flex-1 pb-2">
          <div className="relative flex items-start gap-3 pr-10">
            {/* Stack Image */}
            <div className="h-10 w-10 flex-shrink-0">
              {stack.image ? (
                <img
                  src={stack.image}
                  alt={stack.name}
                  className="w-10 h-10 object-cover rounded-lg"
                />
              ) : (
                <div className="w-10 h-10 grid grid-cols-2 gap-1">
                  <div className="bg-green-500 rounded-sm"></div>
                  <div className="bg-red-500 rounded-sm"></div>
                  <div className="bg-green-400 rounded-sm"></div>
                  <div className="bg-gray-400 rounded-sm"></div>
                </div>
              )}
            </div>
            <div className="space-y-0.5 flex-1 min-w-0 overflow-hidden">
              <TooltipProvider delayDuration={1000}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className={`text-sm sm:text-base leading-5 sm:leading-6 font-semibold truncate ${
                      isDarkBackground ? 'text-white' : 'text-card-foreground'
                    }`}>
                      {stack.name}
                    </h3>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-sm">{stack.name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>

        {/* Compliance Preview Section */}
        <div className="px-6 -mt-5 pb-0">
          <StackCompliancePreview
            complianceSummary={stack.complianceSummary}
            isDarkBackground={isDarkBackground}
          />
        </div>

        <CardFooter className="mt-auto -mt-3 pb-4 px-4 relative flex items-center justify-between">
          {/* Left side - empty for now, could show aggregated team members */}
          <div></div>

          {/* Right side - Space Count */}
          <div className="flex items-center gap-1.5">
            <LayoutGrid className={`h-3.5 w-3.5 ${
              isDarkBackground ? 'text-gray-300' : 'text-gray-600'
            }`} />
            <span className={`text-xs font-medium ${
              isDarkBackground ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {stack.projects.length} space{stack.projects.length !== 1 ? 's' : ''}
            </span>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}